import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  formatBRL,
  formatBytes,
  toLocalInputValue,
  type MediaAsset,
  type Platform,
  type Post,
  type Product,
} from '../api.js';
import { CoverEditor, emptyCover, type CoverConfig } from './CoverEditor.js';
import { CopyAffiliateButton, ViewButton } from './OfferTable.js';
import { Preview, type StickerPosition } from './Preview.js';
import { PageHead } from './PageHead.js';
import { RecurrencePanel, type RecurrenceForm } from './RecurrencePanel.js';
import { BatchPanel } from './BatchPanel.js';

type IgFormat = 'REELS' | 'STORY' | 'FEED';

const PLATFORMS: Array<{ id: Platform; label: string; icon: string }> = [
  { id: 'INSTAGRAM', label: 'Instagram', icon: '📷' },
  { id: 'TIKTOK', label: 'TikTok', icon: '🎵' },
  { id: 'WHATSAPP', label: 'WhatsApp', icon: '💬' },
];

const IG_FORMATS: Array<{ id: IgFormat; label: string; hint: string }> = [
  { id: 'REELS', label: 'Reels', hint: 'Vídeo vertical 9:16, até 90s' },
  { id: 'STORY', label: 'Stories', hint: 'Imagem ou vídeo 9:16, 24h no ar' },
  { id: 'FEED', label: 'Feed', hint: 'Imagem 4:5 ou 1:1' },
];

const STICKER_POSITIONS: Array<{ id: StickerPosition; label: string }> = [
  { id: 'top', label: 'Topo' },
  { id: 'middle', label: 'Meio' },
  { id: 'bottom', label: 'Base' },
];

/** Rascunho independente por plataforma: a mídia de cada rede é diferente. */
type Draft = {
  media: MediaAsset | null;
  caption: string;
  /**
   * Link que a DM da automação entrega no lugar do link do produto.
   *
   * Existe para o post de chamada: "comente EU QUERO para entrar no grupo" —
   * o link é o convite do WhatsApp, não um produto da Shopee.
   */
  dmLink: string;
  scheduledAt: string;
  igFormat: IgFormat;
  stickerLabel: string;
  stickerPosition: StickerPosition;
  useSticker: boolean;
  tiktokPrivacy: string;
  brandedContent: boolean;
  /** Capa do Reels: frame escolhido + título escrito por cima. */
  cover: CoverConfig;
  /** Título de capa sugerido pela IA, a partir do nome do produto. */
  suggestedCoverTitle: string | null;
};

function emptyDraft(): Draft {
  return {
    media: null,
    caption: '',
    dmLink: '',
    scheduledAt: toLocalInputValue(new Date(Date.now() + 3600_000)),
    igFormat: 'REELS',
    stickerLabel: 'VER OFERTA',
    stickerPosition: 'bottom',
    useSticker: true,
    tiktokPrivacy: 'SELF_ONLY',
    brandedContent: true,
    cover: emptyCover(),
    suggestedCoverTitle: null,
  };
}

/**
 * O lote do WhatsApp no armazenamento do navegador.
 *
 * Leitura e escrita protegidas: navegação privada e navegador com dados de site
 * bloqueados fazem o acessador lançar, e uma exceção aqui derrubaria a tela de
 * produção inteira por causa de uma conveniência.
 */
const CHAVE_LOTE = 'lote-whatsapp';

function leLote(): string[] {
  try {
    const cru = localStorage.getItem(CHAVE_LOTE);
    const lista = cru ? (JSON.parse(cru) as unknown) : null;
    return Array.isArray(lista) ? lista.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function gravaLote(ids: string[]): void {
  try {
    localStorage.setItem(CHAVE_LOTE, JSON.stringify(ids));
  } catch {
    // Sem persistência o lote ainda funciona nesta sessão; só não sobrevive ao F5.
  }
}

const copyPlatform: Record<Platform, 'instagram' | 'tiktok' | 'whatsapp'> = {
  INSTAGRAM: 'instagram',
  TIKTOK: 'tiktok',
  WHATSAPP: 'whatsapp',
};

export function Production({ onScheduled }: { onScheduled: () => void }) {
  const [queue, setQueue] = useState<Product[]>([]);
  /**
   * Ids no lote do WhatsApp, na ordem de postagem.
   *
   * Fica no navegador porque é uma escolha de trabalho em andamento, não um
   * dado do negócio: montar o lote e agendar acontece na mesma sessão. Mas
   * sobrevive a um F5 acidental no meio da montagem de 20 itens, que é o
   * acidente que doeria.
   */
  const [loteIds, setLoteIds] = useState<string[]>(() => leLote());
  /**
   * Cupom e parcelamento — dados que a API da Shopee NÃO devolve.
   *
   * Ficam na área de cupons do painel da Shopee, fora da API de afiliado, e a
   * parcela ainda tem juros que variam por produto (medido: de 9% a 18% sobre o
   * à vista). Dividir o preço por 12 daria número errado, e número errado de
   * preço é o pior defeito possível numa mensagem promocional.
   */
  const [couponLabel, setCouponLabel] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [installment, setInstallment] = useState('');

  /**
   * As duas filas derivam da mesma fonte, então nunca podem divergir.
   *
   * `noLote` segue a ordem de `loteIds` (é a ordem dos horários); ids que já não
   * estão na fila — oferta removida da produção, por exemplo — são descartados
   * aqui, sem precisar de limpeza separada.
   */
  const noLote = useMemo(
    () =>
      loteIds
        .map((id) => queue.find((p) => p.id === id))
        .filter((p): p is Product => Boolean(p)),
    [loteIds, queue],
  );
  const naFila = useMemo(
    () => queue.filter((p) => !loteIds.includes(p.id)),
    [queue, loteIds],
  );

  /** Toda mudança de lote passa por aqui, então gravar é impossível esquecer. */
  const mudaLote = useCallback((proximo: (atual: string[]) => string[]) => {
    setLoteIds((atual) => {
      const novo = proximo(atual);
      gravaLote(novo);
      return novo;
    });
  }, []);

  const paraOLote = useCallback(
    (id: string) => mudaLote((atual) => (atual.includes(id) ? atual : [...atual, id])),
    [mudaLote],
  );
  const paraAFila = useCallback(
    (id: string) => mudaLote((atual) => atual.filter((x) => x !== id)),
    [mudaLote],
  );
  const reordenaLote = useCallback(
    (id: string, antesDe: string) =>
      mudaLote((atual) => {
        // Reordenar é sempre "tira e insere": manter o item na posição antiga
        // e copiar para a nova duplicaria o post.
        const sem = atual.filter((x) => x !== id);
        const alvo = sem.indexOf(antesDe);
        if (alvo < 0) return [...sem, id];
        return [...sem.slice(0, alvo), id, ...sem.slice(alvo)];
      }),
    [mudaLote],
  );
  const [active, setActive] = useState<Product | null>(null);
  const [platform, setPlatform] = useState<Platform>('INSTAGRAM');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ kind: 'info' | 'err' | 'warn'; text: string } | null>(
    null,
  );

  // Um rascunho por plataforma. Trocar de aba não perde o que já foi montado.
  const [drafts, setDrafts] = useState<Record<Platform, Draft>>(() => ({
    INSTAGRAM: emptyDraft(),
    TIKTOK: emptyDraft(),
    WHATSAPP: emptyDraft(),
  }));

  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extra, setExtra] = useState('');
  const [scheduled, setScheduled] = useState<Post[]>([]);
  const [affiliateLink, setAffiliateLink] = useState<string | null>(null);

  const draft = drafts[platform];

  function patchDraft(patch: Partial<Draft>) {
    setDrafts((prev) => ({ ...prev, [platform]: { ...prev[platform], ...patch } }));
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.products({ status: 'SELECTED', take: 100 });
      setQueue(data.items);
      setActive((current) =>
        current
          ? (data.items.find((p) => p.id === current.id) ?? data.items[0] ?? null)
          : (data.items[0] ?? null),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Ao trocar de oferta: zera os três rascunhos e busca o que já foi agendado.
  useEffect(() => {
    if (!active) return;
    setDrafts({ INSTAGRAM: emptyDraft(), TIKTOK: emptyDraft(), WHATSAPP: emptyDraft() });
    setExtra('');
    setMessage(null);
    setAffiliateLink(null);
    void api
      .posts({ productId: active.id })
      .then(setScheduled)
      .catch(() => setScheduled([]));
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Plataformas que já têm post agendado para a oferta ativa. */
  const doneByPlatform = useMemo(() => {
    const map = new Map<Platform, number>();
    for (const post of scheduled) {
      if (post.status === 'CANCELED') continue;
      for (const pub of post.publications) {
        map.set(pub.platform, (map.get(pub.platform) ?? 0) + 1);
      }
    }
    return map;
  }, [scheduled]);

  async function upload(file: File) {
    setUploading(true);
    setMessage(null);
    try {
      const asset = await api.uploadMedia(file);
      patchDraft({ media: asset });
      if (asset.warning) setMessage({ kind: 'warn', text: asset.warning });

      // Avisos de formato na hora do upload — melhor que descobrir ao publicar.
      if (platform === 'TIKTOK' && asset.kind === 'IMAGE') {
        setMessage({ kind: 'warn', text: 'O TikTok exige vídeo. Esta imagem não será aceita.' });
      }
      if (platform === 'INSTAGRAM' && draft.igFormat === 'REELS' && asset.kind === 'IMAGE') {
        patchDraft({ media: asset, igFormat: 'FEED' });
        setMessage({
          kind: 'warn',
          text: 'Reels exige vídeo — mudamos o formato para Feed, que aceita imagem.',
        });
      }
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Falha no upload',
      });
    } finally {
      setUploading(false);
    }
  }

  async function generateCopy() {
    if (!active) return;
    setGenerating(true);
    setMessage(null);
    try {
      const result = await api.generateCopy({
        productId: active.id,
        platform: copyPlatform[platform],
        tone: 'informal',
        ...(extra.trim() ? { extraInstructions: extra.trim() } : {}),
        couponLabel: couponLabel.trim() || null,
        couponCode: couponCode.trim() || null,
        installmentLabel: installment.trim() || null,
      });
      // A IA devolve UMA copy: vai direto para o campo de edição. A etapa de
      // "escolher entre variações" saiu — três parecidas obrigavam a decidir
      // sem critério.
      patchDraft({
        caption: result.variants[0] ?? '',
        ...(result.structured
          ? { suggestedCoverTitle: result.structured.coverTitle }
          : {}),
      });
      if (result.model === 'mock') {
        setMessage({
          kind: 'info',
          text: 'Copy de exemplo (modo mock). Configure ANTHROPIC_API_KEY para usar a IA real.',
        });
      }
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Falha ao gerar copy',
      });
    } finally {
      setGenerating(false);
    }
  }

  // O preview do WhatsApp mostra o card do link; buscar o link real deixa a
  // prévia fiel em vez de um placeholder.
  useEffect(() => {
    if (!active || platform !== 'WHATSAPP' || affiliateLink) return;
    void api
      .affiliateLink(active.id, 'whatsapp')
      .then((r) => setAffiliateLink(r.shortLink))
      .catch(() => undefined);
  }, [active?.id, platform, affiliateLink]); // eslint-disable-line react-hooks/exhaustive-deps

  const isStory = platform === 'INSTAGRAM' && draft.igFormat === 'STORY';
  const isStoryWithLink = isStory && draft.useSticker;

  const blockers: string[] = [];
  if (!active) blockers.push('Selecione uma oferta');
  // Stories não tem legenda: o texto ali é opcional (vai na mensagem de apoio).
  if (!draft.caption.trim() && !isStory) blockers.push('Escreva ou gere o texto');
  // No WhatsApp a mídia é opcional: mensagem só com texto e link funciona, e
  // muita divulgação de achadinho é exatamente isso. Instagram e TikTok são
  // redes de mídia — ali ela continua obrigatória.
  if (!draft.media && platform !== 'WHATSAPP') blockers.push('Envie a mídia');
  if (platform === 'TIKTOK' && draft.media?.kind !== 'VIDEO') blockers.push('TikTok exige vídeo');
  if (platform === 'INSTAGRAM' && draft.igFormat === 'REELS' && draft.media?.kind !== 'VIDEO') {
    blockers.push('Reels exige vídeo');
  }
  if (!draft.scheduledAt) blockers.push('Defina data e hora');

  /**
   * Opções específicas da rede, do rascunho atual.
   *
   * Extraída porque o agendamento avulso e a programação recorrente têm de
   * mandar exatamente as mesmas — duas cópias divergiriam na primeira mudança.
   */
  function buildOptions(): Record<string, unknown> {
    const options: Record<string, unknown> = {};
    if (platform === 'INSTAGRAM') {
      options.format = draft.igFormat;
      // Capa própria tem precedência na API; só mandamos o offset quando não
      // há imagem gerada, para não enviar dois caminhos concorrentes.
      if (draft.igFormat === 'REELS') {
        if (draft.cover.generated) {
          options.coverUrl = draft.cover.generated.publicUrl;
          options.coverTitle = draft.cover.title;
        } else if (draft.cover.thumbOffsetMs > 0) {
          options.thumbOffsetMs = draft.cover.thumbOffsetMs;
        }
      }
      if (isStoryWithLink) {
        options.linkSticker = {
          label: draft.stickerLabel.trim() || 'VER OFERTA',
          position: draft.stickerPosition,
        };
      }
    }
    if (platform === 'TIKTOK') {
      options.privacyLevel = draft.tiktokPrivacy;
      options.brandedContent = draft.brandedContent;
    }
    return options;
  }

  /**
   * Cria a programação recorrente com o mesmo rascunho do post avulso.
   *
   * Não exige data e hora: a hora vem dos horários da programação. Por isso os
   * impedimentos passados ao painel tiram justamente esse item.
   */
  async function criarProgramacao(form: RecurrenceForm) {
    if (!active) return;
    setSaving(true);
    setMessage(null);
    try {
      const r = await api.createRecurrence({
        productId: active.id,
        mediaAssetId: draft.media?.id ?? null,
        caption: draft.caption,
        platforms: [platform],
        options: { [platform]: buildOptions() },
        times: form.times,
        weekdays: form.weekdays,
        endsOn:
          form.dias === null
            ? null
            : new Date(Date.now() + form.dias * 86_400_000).toISOString(),
      });
      setScheduled(await api.posts({ productId: active.id }));
      const prazo = form.dias === null ? 'sem data de fim' : `pelos próximos ${form.dias} dias`;
      setMessage({
        kind: 'info',
        text:
          `Programação criada: ${form.times.join(', ')} ${prazo}. ` +
          `Os ${r.postsCriados} primeiros posts já estão na Agenda — o resto entra conforme ` +
          'a data se aproxima.',
      });
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Falha ao criar a programação',
      });
    } finally {
      setSaving(false);
    }
  }

  async function schedule() {
    if (!active || blockers.length > 0) return;
    setSaving(true);
    setMessage(null);
    try {
      const options = buildOptions();

      await api.createPost({
        productId: active.id,
        mediaAssetId: draft.media?.id ?? null,
        caption: draft.caption,
        dmLink: platform === 'INSTAGRAM' && draft.dmLink.trim() ? draft.dmLink.trim() : null,
        scheduledAt: new Date(draft.scheduledAt).toISOString(),
        platforms: [platform],
        options: { [platform]: options } as never,
      });

      // A oferta FICA na fila: a ideia é agendar as outras redes em seguida.
      setDrafts((prev) => ({ ...prev, [platform]: emptyDraft() }));
      setScheduled(await api.posts({ productId: active.id }));
      setMessage({
        kind: 'info',
        text: `Agendado no ${PLATFORMS.find((p) => p.id === platform)!.label}. A oferta continua aqui para você agendar nas outras redes.`,
      });
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Falha ao agendar',
      });
    } finally {
      setSaving(false);
    }
  }

  /** Tira a oferta da fila de produção, sem mexer no que já foi agendado. */
  async function removeFromQueue(product: Product, event: React.MouseEvent) {
    event.stopPropagation();
    const jaAgendou = (product._count?.posts ?? 0) > 0;
    const aviso = jaAgendou
      ? `“${product.name}” já tem publicação agendada.\n\nRemover daqui NÃO cancela o que foi agendado — só tira da fila de produção. Continuar?`
      : `Remover “${product.name}” da fila de produção?`;
    if (!window.confirm(aviso)) return;

    const restante = queue.filter((p) => p.id !== product.id);
    setQueue(restante);
    if (active?.id === product.id) setActive(restante[0] ?? null);

    try {
      await api.setProductStatus(product.id, 'DISCARDED');
    } catch {
      await load(); // servidor recusou: volta ao estado real
    }
  }

  if (loading) {
    return (
      <div className="empty">
        <span className="spinner" /> Carregando fila de produção…
      </div>
    );
  }

  if (queue.length === 0) {
    return (
      <>
        <PageHead
          eyebrow="Produção"
          title="Nada esperando mídia"
          description="Aqui ficam as ofertas selecionadas, aguardando mídia e texto."
        />
        <div className="card empty">
          <strong>Nada em produção</strong>
          Vá em Ofertas e selecione o que você vai divulgar.
        </div>
      </>
    );
  }

  const platformLabel = PLATFORMS.find((p) => p.id === platform)!.label;
  const temSecaoExtra =
    isStory ||
    platform === 'TIKTOK' ||
    (platform === 'INSTAGRAM' && draft.igFormat === 'REELS' && draft.media !== null);
  const passoTexto = temSecaoExtra ? 3 : 2;

  return (
    <>
      <PageHead
        eyebrow={`Produção · ${platformLabel}`}
        title="Montar o post"
        description="Cada rede tem sua própria mídia e texto — vídeo no TikTok, foto no Feed, o que fizer sentido. Agende uma por vez; a oferta continua na fila para as outras."
        readout={[
          { label: queue.length === 1 ? 'oferta na fila' : 'ofertas na fila', value: queue.length },
          // Quantas redes desta oferta já estão agendadas: é o que evita
          // postar duas vezes no mesmo lugar sem perceber.
          active && {
            label: 'de 3 redes agendadas',
            value: doneByPlatform.size,
            tom: doneByPlatform.size > 0 ? ('gain' as const) : ('quiet' as const),
          },
          blockers.length > 0 && {
            label: blockers.length === 1 ? 'passo faltando' : 'passos faltando',
            value: blockers.length,
            tom: 'quiet' as const,
          },
        ]}
        actions={
          <button type="button" className="ghost" onClick={onScheduled}>
            Ver agenda →
          </button>
        }
      />

      {message && <div className={`notice ${message.kind}`}>{message.text}</div>}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(210px, 250px) minmax(0, 1fr) minmax(0, 310px)',
          gap: 16,
          alignItems: 'start',
        }}
      >
        {/* ------------------------------------------------ fila de ofertas */}
        <div className="card">
          <div style={{ padding: '11px 12px', borderBottom: '1px solid var(--border)' }}>
            <p className="section-title" style={{ margin: 0 }}>
              Na fila ({naFila.length})
              {noLote.length > 0 && (
                <span className="muted" style={{ fontWeight: 400 }}>
                  {' '}
                  · {noLote.length} no lote do WhatsApp
                </span>
              )}
            </p>
          </div>
          <div
            style={{ maxHeight: '70vh', overflowY: 'auto' }}
            // Soltar na fila devolve do lote: o caminho de volta tem de ser tão
            // fácil quanto o de ida, senão errar um arraste custa recomeçar.
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const id =
                e.dataTransfer.getData('application/x-oferta') ||
                e.dataTransfer.getData('text/plain');
              if (id) paraAFila(id);
            }}
          >
            {naFila.map((product) => (
              <div
                key={product.id}
                className="queue-item"
                aria-current={active?.id === product.id ? 'true' : undefined}
                role="button"
                tabIndex={0}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/x-oferta', product.id);
                  e.dataTransfer.setData('text/plain', product.id);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onClick={() => setActive(product)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActive(product);
                  }
                }}
              >
                {product.imageUrl && (
                  <img
                    src={product.imageUrl}
                    alt=""
                    style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover' }}
                  />
                )}
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 12.5,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: active?.id === product.id ? 'var(--accent)' : 'var(--text)',
                    }}
                  >
                    {product.name}
                  </span>
                  <span className="muted" style={{ fontSize: 11.5 }}>
                    {product.kind === 'PRODUCT' ? formatBRL(product.priceCents) : 'oferta'}
                    {(product._count?.posts ?? 0) > 0 && (
                      <span style={{ color: 'var(--ok)', fontWeight: 600 }}>
                        {' · '}
                        {product._count?.posts} agendado
                        {(product._count?.posts ?? 0) > 1 ? 's' : ''}
                      </span>
                    )}
                  </span>
                </span>
                <button
                  type="button"
                  className="queue-move"
                  onClick={(e) => {
                    // Sem o stopPropagation o clique também selecionaria a
                    // oferta no editor, trocando a tela sob o dedo.
                    e.stopPropagation();
                    paraOLote(product.id);
                  }}
                  title="Mandar para o lote do WhatsApp"
                  aria-label={`Mandar ${product.name} para o lote do WhatsApp`}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="queue-remove"
                  onClick={(e) => void removeFromQueue(product, e)}
                  title="Remover da fila de produção"
                  aria-label={`Remover ${product.name} da fila`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          {/* Segunda fila, abaixo da primeira: é o destino do arraste. */}
          <BatchPanel
            items={noLote}
            disponiveis={naFila.length}
            onDropProduct={paraOLote}
            onRemove={paraAFila}
            onReorder={reordenaLote}
            onScheduled={async () => {
              // A fila de produção NÃO muda (as ofertas continuam para
              // IG/TikTok); o lote esvazia, porque já virou agenda.
              mudaLote(() => []);
              if (active) setScheduled(await api.posts({ productId: active.id }));
              await load();
            }}
          />
        </div>

        {/* ---------------------------------------------------- editor */}
        {active && (
          <div className="card" style={{ padding: 18 }}>
            <div className="plat-tabs">
              {PLATFORMS.map((p) => {
                const feitos = doneByPlatform.get(p.id) ?? 0;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className="plat-tab"
                    aria-current={platform === p.id ? 'page' : undefined}
                    onClick={() => setPlatform(p.id)}
                    title={feitos > 0 ? `${feitos} publicação(ões) já agendada(s)` : undefined}
                  >
                    {p.icon} {p.label}
                    {feitos > 0 && <span className="dot" />}
                  </button>
                );
              })}
            </div>

            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                marginBottom: 16,
                flexWrap: 'wrap',
              }}
            >
              <strong style={{ fontSize: 13 }}>{active.name}</strong>
              <ViewButton offer={active} />
              <CopyAffiliateButton offer={active} />
            </div>

            {platform === 'INSTAGRAM' && (
              <>
                <p className="section-title">Formato</p>
                <div className="checks" style={{ marginBottom: 16 }}>
                  {IG_FORMATS.map((f) => (
                    <label key={f.id} className="check" title={f.hint}>
                      <input
                        type="radio"
                        name="igformat"
                        checked={draft.igFormat === f.id}
                        onChange={() => patchDraft({ igFormat: f.id })}
                      />
                      {f.label}
                    </label>
                  ))}
                </div>
              </>
            )}

            <p className="section-title">
              1. Mídia desta rede
              {platform === 'WHATSAPP' && (
                <span className="muted" style={{ fontWeight: 400 }}> — opcional</span>
              )}
            </p>
            {platform === 'WHATSAPP' && !draft.media && (
              <p className="muted" style={{ margin: '0 0 10px', fontSize: 12.5 }}>
                Sem mídia, a mensagem sai só com o texto e o link — o WhatsApp gera a
                pré-visualização do produto sozinho.
              </p>
            )}
            <div className="row" style={{ alignItems: 'center', marginBottom: 18 }}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void upload(file);
                }}
                disabled={uploading}
              />
              {uploading && (
                <span className="muted" style={{ flex: 'none' }}>
                  <span className="spinner" /> enviando…
                </span>
              )}
              {draft.media && (
                <span className="badge ok" style={{ flex: 'none' }}>
                  {draft.media.kind === 'VIDEO' ? '🎬 vídeo' : '🖼 imagem'} ·{' '}
                  {formatBytes(draft.media.sizeBytes)}
                </span>
              )}
            </div>

            {platform === 'INSTAGRAM' && draft.igFormat === 'REELS' && draft.media && (
              <>
                <p className="section-title">2. Capa do Reels</p>
                <div style={{ marginBottom: 18 }}>
                  <CoverEditor
                    media={draft.media}
                    cover={draft.cover}
                    suggestedTitle={draft.suggestedCoverTitle}
                    onChange={(patch) =>
                      patchDraft({ cover: { ...draft.cover, ...patch } })
                    }
                  />
                </div>
              </>
            )}

            {isStory && (
              <>
                <p className="section-title">2. Sticker de link</p>
                <label className="check" style={{ marginBottom: 10 }}>
                  <input
                    type="checkbox"
                    checked={draft.useSticker}
                    onChange={(e) => patchDraft({ useSticker: e.target.checked })}
                  />
                  Incluir sticker de link no Story
                </label>

                {draft.useSticker && (
                  <>
                    <div className="row" style={{ marginBottom: 10 }}>
                      <div>
                        <label htmlFor="stk">Texto do botão</label>
                        <input
                          id="stk"
                          value={draft.stickerLabel}
                          onChange={(e) => patchDraft({ stickerLabel: e.target.value })}
                          maxLength={28}
                          placeholder="VER OFERTA"
                        />
                      </div>
                      <div>
                        <label htmlFor="stkpos">Posição</label>
                        <select
                          id="stkpos"
                          value={draft.stickerPosition}
                          onChange={(e) =>
                            patchDraft({ stickerPosition: e.target.value as StickerPosition })
                          }
                        >
                          {STICKER_POSITIONS.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Limitação real da plataforma, dita de frente. */}
                    <div className="notice warn" style={{ fontSize: 12.5 }}>
                      <strong>A API do Instagram não adiciona stickers.</strong> Ela publica
                      Stories, mas o sticker de link precisa ser colocado por você no app. Então
                      este Story vai para a Agenda como <em>“postar à mão”</em>: no horário você
                      recebe tudo pronto e publica em segundos. O preview ao lado serve para
                      desenhar a arte sem informação importante embaixo do botão.
                    </div>
                  </>
                )}
              </>
            )}

            {platform === 'TIKTOK' && (
              <>
                <p className="section-title">2. Opções do TikTok</p>
                <div className="row" style={{ marginBottom: 18 }}>
                  <div>
                    <label htmlFor="ttPrivacy">Privacidade</label>
                    <select
                      id="ttPrivacy"
                      value={draft.tiktokPrivacy}
                      onChange={(e) => patchDraft({ tiktokPrivacy: e.target.value })}
                    >
                      <option value="SELF_ONLY">Só eu (antes do audit)</option>
                      <option value="MUTUAL_FOLLOW_FRIENDS">Amigos</option>
                      <option value="PUBLIC_TO_EVERYONE">Público</option>
                    </select>
                  </div>
                  <label className="check" style={{ alignSelf: 'flex-end', paddingBottom: 8 }}>
                    <input
                      type="checkbox"
                      checked={draft.brandedContent}
                      onChange={(e) => patchDraft({ brandedContent: e.target.checked })}
                    />
                    Conteúdo comercial (obrigatório com link de afiliado)
                  </label>
                </div>
              </>
            )}

            {/* Stories não tem legenda.
                O texto não ia a lugar nenhum na publicação — só aparecia na
                prévia como referência. Uma caixa que não publica nada convida a
                escrever para o vazio, e o aviso ao lado do título não impedia
                ninguém de perder tempo ali. */}
            {!isStory && (
              <p className="section-title">{passoTexto}. Texto</p>
            )}
            {platform === 'WHATSAPP' && (
              <div className="row" style={{ marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: '0 0 150px' }}>
                  <label htmlFor="cup-label">Cupom (opcional)</label>
                  <input
                    id="cup-label"
                    value={couponLabel}
                    onChange={(e) => setCouponLabel(e.target.value)}
                    placeholder="R$100 0FF"
                  />
                </div>
                <div style={{ flex: '0 0 140px' }}>
                  <label htmlFor="cup-code">Código do cupom</label>
                  <input
                    id="cup-code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="L4NCM8SHAF"
                  />
                </div>
                <div style={{ flex: '0 0 150px' }}>
                  <label htmlFor="parc">Parcelamento</label>
                  <input
                    id="parc"
                    value={installment}
                    onChange={(e) => setInstallment(e.target.value)}
                    placeholder="12x R$123,28"
                  />
                </div>
                <p
                  className="muted"
                  style={{ flex: 1, minWidth: 200, fontSize: 12, alignSelf: 'flex-end', margin: 0 }}
                >
                  A Shopee não devolve cupom nem parcela pela API — copie do painel dela.
                  Em branco, o bloco simplesmente não aparece na mensagem.
                </p>
              </div>
            )}

            {platform === 'INSTAGRAM' && !isStory && (
              <div style={{ marginBottom: 10 }}>
                <label htmlFor="dm-link">
                  Link da DM automática (opcional)
                </label>
                <input
                  id="dm-link"
                  value={draft.dmLink}
                  onChange={(e) => patchDraft({ dmLink: e.target.value })}
                  placeholder="ex: https://chat.whatsapp.com/XXXX (convite do grupo)"
                />
                <p className="muted" style={{ fontSize: 12, margin: '5px 0 0' }}>
                  Quem comentar o gatilho neste post recebe <strong>este link</strong> na
                  DM, no lugar do link do produto. Use para chamar para o grupo do
                  WhatsApp. Vazio = link de afiliado do produto, como sempre.
                </p>
              </div>
            )}

            {!isStory && (
              <div className="row" style={{ marginBottom: 10 }}>
                <div style={{ flex: 2 }}>
                  <label htmlFor="extra">Instrução extra para a IA (opcional)</label>
                  <input
                    id="extra"
                    value={extra}
                    onChange={(e) => setExtra(e.target.value)}
                    placeholder="ex: público fitness, mencionar frete grátis"
                  />
                </div>
                <button
                  type="button"
                  onClick={generateCopy}
                  disabled={generating}
                  style={{ flex: 'none', alignSelf: 'flex-end' }}
                >
                  {generating ? <span className="spinner" /> : '✨ Gerar com IA'}
                </button>
              </div>
            )}

            {!isStory && (
              <textarea
                value={draft.caption}
                onChange={(e) => patchDraft({ caption: e.target.value })}
                placeholder="Escreva o texto ou gere com a IA e edite aqui…"
                style={{ marginBottom: 18 }}
              />
            )}

            <p className="section-title">Quando publicar</p>
            <div className="row" style={{ marginBottom: 16 }}>
              <div>
                <label htmlFor="when">Data e hora</label>
                <input
                  id="when"
                  type="datetime-local"
                  value={draft.scheduledAt}
                  onChange={(e) => patchDraft({ scheduledAt: e.target.value })}
                />
              </div>
            </div>

            {blockers.length > 0 && (
              <div className="notice warn">Falta: {blockers.join(' · ')}</div>
            )}

            <button
              type="button"
              className="primary"
              onClick={schedule}
              disabled={saving || blockers.length > 0}
              style={{ width: '100%', padding: 10 }}
            >
              {saving ? <span className="spinner" /> : `Agendar no ${platformLabel}`}
            </button>

            {(doneByPlatform.get(platform) ?? 0) > 0 && (
              <p className="muted" style={{ marginTop: 10, textAlign: 'center' }}>
                Esta oferta já tem {doneByPlatform.get(platform)} publicação(ões) agendada(s) nesta
                rede.
              </p>
            )}

            <div style={{ marginTop: 18 }}>
              {/* A programação não precisa de data e hora — a hora vem dela.
                  Por isso esse impedimento sai da lista repassada ao painel. */}
              <RecurrencePanel
                platform={platform}
                blockers={blockers.filter((b) => b !== 'Defina data e hora')}
                saving={saving}
                onCreate={criarProgramacao}
              />
            </div>
          </div>
        )}

        {/* ---------------------------------------------------- preview */}
        {active && (
          <div style={{ position: 'sticky', top: 76 }}>
            <p className="section-title" style={{ marginBottom: 10 }}>
              Prévia —{' '}
              {platform === 'INSTAGRAM'
                ? IG_FORMATS.find((f) => f.id === draft.igFormat)!.label
                : platformLabel}
            </p>
            <Preview
              platform={platform}
              format={draft.igFormat}
              product={active}
              media={draft.media}
              caption={draft.caption}
              affiliateLink={affiliateLink}
              sticker={
                isStoryWithLink
                  ? { label: draft.stickerLabel, position: draft.stickerPosition }
                  : null
              }
              accountName="sua_conta"
            />
            <p className="muted" style={{ marginTop: 10, fontSize: 11.5, maxWidth: 290 }}>
              Referência de enquadramento e de onde o texto corta — não é o app real.
            </p>
          </div>
        )}
      </div>
    </>
  );
}
